









































import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { DxTextBox } from 'devextreme-vue/text-box';
import { DxValidator, DxRequiredRule, DxAsyncRule } from 'devextreme-vue/validator';
import { DxValidationGroup } from 'devextreme-vue/validation-group';
import { BatchDetailsDto } from '@/models/batch/BatchDetailsDto';
import { BatchService } from '@/service/BatchService';
import { BatchNameValidationParameters } from '@/models/batch/BatchNameValidationParameters';
import { ValidationResultDto } from '@/models/ValidationResultDto';
import { BatchDto } from '@/models/batch/BatchDto';

@Component({
  components: {
    DxAsyncRule,
    DxPopup,
    DxRequiredRule,
    DxTextBox,
    DxToolbarItem,
    DxValidationGroup,
    DxValidator
  }
})
export default class BatchRename extends Vue {
  private readonly batchService: BatchService = new BatchService();
  readonly validationGroupRefKey: string = 'validationGroup';
  batchName: string | null = null;
  private bypassServerValidation: boolean = true;

  @Prop({ required: true })
  batch!: BatchDetailsDto;

  @Prop({ required: true })
  visible!: boolean;

  get title() {
    return `Rename Batch ${this.batch.BatchName}`;
  }

  get saveButtonOptions() {
    return {
      text: 'Save',
      type: 'default',
      stylingMode: 'contained',
      onClick: this.saveAndClose,
      useSubmitBehavior: true
    };
  }

  get cancelButtonOptions() {
    return {
      text: 'Cancel',
      type: 'normal',
      stylingMode: 'contained',
      onClick: this.cancelAndClose
    };
  }

  get validationGroup() {
    let validationGroupRef = this.$refs[this.validationGroupRefKey];
    if (validationGroupRef != null) {
      return (validationGroupRef as DxValidationGroup).instance;
    }

    return undefined;
  }

  created() {
    document.addEventListener('keyup', this.onKeyup, true);
    document.addEventListener('keydown', this.onKeydown, true);
  }

  destroyed() {
    document.removeEventListener('keyup', this.onKeyup, true);
    document.removeEventListener('keydown', this.onKeydown, true);
  }

  validateBatchNameAsync(params: any) {
    if (this.bypassServerValidation || params.value === this.batch.BatchName) {
      return Promise.resolve({
        isValid: true,
        message: ''
      });
    }

    let parameters: BatchNameValidationParameters = {
      ClientId: this.batch.ClientId,
      BatchName: params.value
    };

    return this.batchService.validateBatchName(parameters)
      .then(result => {
        return {
          isValid: result.IsValid,
          message: result.Message
        };
      })
      .catch(reason => {
        return {
          isValid: false,
          message: 'An error occurred validating the batch name'
        };
      });
  }

  private saveAndClose() {
    if (this.validationGroup) {
      this.bypassServerValidation = false;

      let result = this.validationGroup.validate() as any;
      if (result.status === 'pending') {
        result.complete.then((validationResult: any) => {
          if (validationResult.isValid === true) {
            this.submitSaveAndClose();
          }
        });
      } else if (result.isValid === true) {
        this.submitSaveAndClose();
      }

      this.bypassServerValidation = true;
    } else {
      this.submitSaveAndClose();
    }
  }

  private submitSaveAndClose() {
    this.save();
    this.close();
  }

  private cancelAndClose() {
    this.close();
  }

  private save() {
    let changesMade = false;

    if (this.batchName != null && this.batchName !== this.batch.BatchName) {
      this.batch.BatchName = this.batchName;
      changesMade = true;
    }

    if (changesMade) {
      let data: BatchDto = {
        BatchId: this.batch.BatchId,
        BatchName: this.batch.BatchName,
        BatchTypeId: this.batch.BatchTypeId
      };

      this.batchService.updateBatch(this.batch.BatchId, data);
    }
  }

  onShowing() {
    this.initialize();
  }

  onHiding() {
    this.close();
  }

  onHidden() {
    this.reset();
  }

  private initialize() {
    this.batchName = this.batch.BatchName;
    this.bypassServerValidation = true;
  }

  private reset() {
    this.batchName = null;
    this.bypassServerValidation = true;
  }

  private close() {
    this.$emit('hiding');
  }

  private onKeydown(e: KeyboardEvent) {
    if (this.isSaveShortcut(e)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  private onKeyup(e: KeyboardEvent) {
    if (this.isSaveShortcut(e)) {
      e.preventDefault();
      e.stopPropagation();
      this.saveAndClose();
    }
  }

  private isSaveShortcut(e: KeyboardEvent) {
    return e.key === 'Enter' && (e.ctrlKey || e.shiftKey);
  }

}
